/**
 * Wish List — directed-gift inventory.
 *
 * Each item is a discrete need the Foundation has, sized so a single
 * supporter can fully fund it OR contribute partially toward it. Total
 * (~US$23K) sits inside, not in addition to, the US$320K ICANN campaign:
 * supporting a Wish List item is a way of directing where general support
 * goes, not a separate fundraise.
 *
 * `raised_usd` is updated manually as wires arrive, in commit-visible diff.
 * That manual cadence IS the transparency mechanism during the stewardship
 * period — every change to a number on this page is a signed commit on the
 * public repository.
 *
 * `purchase_url` lets a supporter buy a tangible item directly and ship it
 * to the steward (Mind Hack, Inc.) at the Delaware address. For services
 * (legal, social-media), `purchase_url` is undefined and the card CTA reads
 * "Coordinate this gift" instead of "Buy & ship to steward".
 */

export type WishlistCategory =
  | "compute"
  | "infrastructure"
  | "operations"
  | "legal"
  | "services";

export type WishlistCadence = "one-time" | "monthly" | "annual";

export type WishlistStatus = "open" | "in_progress" | "fulfilled";

export interface WishlistItem {
  /** Slug used as memo code (e.g. "dgx-spark" → memo "DPF-WISH-DGX-SPARK"). */
  id: string;
  name: string;
  short: string;
  category: WishlistCategory;
  cost_usd: number;
  raised_usd: number;
  cadence: WishlistCadence;
  /** For monthly items: total months covered by `cost_usd`. */
  duration_months?: number;
  why_this: string;
  what_it_unlocks: string;
  /** Amazon / manufacturer / B&H / NVIDIA URL where a supporter can buy and ship. */
  purchase_url?: string;
  vendor: string;
  accepts_partial: boolean;
  /** True for tangible goods that can be shipped to the steward; false for services. */
  accepts_in_kind: boolean;
  status: WishlistStatus;
}

export const WISHLIST_ITEMS: readonly WishlistItem[] = [
  {
    id: "dgx-spark",
    name: "NVIDIA DGX Spark",
    short: "Sovereign AI compute on the desk",
    category: "compute",
    cost_usd: 4_699,
    raised_usd: 0,
    cadence: "one-time",
    why_this:
      "Local AI compute lets the Foundation fine-tune and evaluate models on .person corpus snapshots without routing sensitive material through third-party APIs. Sovereignty is foundational to the protocol; the Foundation should practise it.",
    what_it_unlocks:
      "End-to-end personhood-data experimentation that never leaves Foundation custody, plus a reference machine for documenting the on-device runtime path described in the Phase 2 roadmap.",
    purchase_url:
      "https://marketplace.nvidia.com/en-us/enterprise/personal-ai-supercomputers/dgx-spark/",
    vendor: "NVIDIA Marketplace",
    accepts_partial: true,
    accepts_in_kind: true,
    status: "open",
  },
  {
    id: "mac-studio",
    name: "Mac Studio (M4 Max, 64GB)",
    short: "Quiet companion workstation for dense local inference",
    category: "compute",
    cost_usd: 2_499,
    raised_usd: 0,
    cadence: "one-time",
    why_this:
      "Companion to the DGX Spark for non-CUDA workloads. MLX runs 70B-class models locally for design-time experimentation; quiet enough to share an office, fast enough to host the Foundation's daily build pipeline.",
    what_it_unlocks:
      "A second sovereign-compute node — redundancy if one machine is down, parallelism if both are up. Doubles as the typesetting and design station for whitepapers and ICANN materials.",
    purchase_url:
      "https://www.apple.com/shop/buy-mac/mac-studio/m4-max-chip-16-core-cpu-40-core-gpu-64gb-memory-512gb-storage",
    vendor: "Apple",
    accepts_partial: true,
    accepts_in_kind: true,
    status: "open",
  },
  {
    id: "synology-nas",
    name: "Synology DS923+ NAS (with 4×4TB drives)",
    short: "On-site storage for weights, datasets, and corpus snapshots",
    category: "infrastructure",
    cost_usd: 1_400,
    raised_usd: 0,
    cadence: "one-time",
    why_this:
      "Stores model weights, datasets, .person corpus snapshots, and a local mirror of the GitHub repositories. Cheaper over a three-year horizon than equivalent cloud storage and builds owned equity in the Foundation's infrastructure.",
    what_it_unlocks:
      "Versioned, redundant, off-LAN-backed storage for everything that should not live solely on a workstation drive. Becomes the Foundation's archival substrate.",
    purchase_url:
      "https://www.amazon.com/Synology-Diskstation-RyzenTM-Dual-Core-LAN-Port/dp/B0BN3959DG",
    vendor: "Amazon",
    accepts_partial: true,
    accepts_in_kind: true,
    status: "open",
  },
  {
    id: "apc-ups",
    name: "APC Smart-UPS 1500VA with Network Management Card",
    short: "Power resilience for compute and the OpenClaw stack",
    category: "infrastructure",
    cost_usd: 650,
    raised_usd: 0,
    cadence: "one-time",
    why_this:
      "Lusaka load-shedding regularly takes residential power offline. Compute, NAS, and the OpenClaw WhatsApp stack need uninterrupted runtime. The Network Management Card also enables remote graceful shutdown when battery thresholds are crossed.",
    what_it_unlocks:
      "Reliable up-time for the Foundation's local services through scheduled outages and unscheduled drops. No more lost work because the grid blinked.",
    purchase_url:
      "https://www.amazon.com/APC-SmartConnect-SMT1500CNC-Interactive-Uninterruptible/dp/B0C8ZQQZ6D",
    vendor: "Amazon",
    accepts_partial: true,
    accepts_in_kind: true,
    status: "open",
  },
  {
    id: "netgear-m6-pro",
    name: "Netgear Nighthawk M6 Pro 5G Mobile Router",
    short: "Connectivity failover when fibre drops",
    category: "infrastructure",
    cost_usd: 800,
    raised_usd: 0,
    cadence: "one-time",
    why_this:
      "The protocol's reference services should not have uptime tied to a single ISP. A 5G failover router keeps the Foundation reachable when fibre drops, which it does. Unlocked, so the same hardware works on any carrier.",
    what_it_unlocks:
      "Continuous reachability for hello@dotperson.org, the GitHub mirror, and the OpenClaw stack — independent of any one upstream provider.",
    purchase_url:
      "https://www.amazon.com/NETGEAR-Nighthawk-Unlocked-International-Countries/dp/B0C2ZP2DXH",
    vendor: "Amazon",
    accepts_partial: true,
    accepts_in_kind: true,
    status: "open",
  },
  {
    id: "dell-u3225qe",
    name: 'Dell UltraSharp 32" 4K Monitor (U3225QE)',
    short: "A second 4K display for typesetting and dense review",
    category: "operations",
    cost_usd: 900,
    raised_usd: 0,
    cadence: "one-time",
    why_this:
      "Foundation work spans typesetting whitepapers, reading dense legal text, and reviewing schema diff. A second 4K display materially changes how much focused work fits in a day.",
    what_it_unlocks:
      "Side-by-side review of spec, code, and prose at full resolution. Whitepaper revisions stop requiring printout.",
    purchase_url:
      "https://www.amazon.com/Dell-UltraSharp-32-inch-Thunderbolt-Monitor/dp/B0FDCLPZC6",
    vendor: "Amazon",
    accepts_partial: true,
    accepts_in_kind: true,
    status: "open",
  },
  {
    id: "steelcase-series-1",
    name: "Steelcase Series 1 ergonomic chair",
    short: "A real chair for full-time desk work",
    category: "operations",
    cost_usd: 500,
    raised_usd: 0,
    cadence: "one-time",
    why_this:
      "Founder is full-time at a desk. A real chair is the single highest-ROI piece of office equipment for any knowledge worker measured in pain-free working years. The Series 1 is the entry point of Steelcase's ergonomic line and ships CarbonNeutral-certified.",
    what_it_unlocks:
      "Sustainable working posture for the long arc of the campaign, ICANN evaluation period, and beyond. Genuinely the boring item that pays back daily.",
    purchase_url:
      "https://store.steelcase.com/seating/ergonomic-chairs/steelcase-series-1",
    vendor: "Steelcase Store",
    accepts_partial: true,
    accepts_in_kind: true,
    status: "open",
  },
  {
    id: "logitech-mx-combo",
    name: "Logitech MX Master 3S + MX Keys S Combo",
    short: "Daily-driver wireless peripherals",
    category: "operations",
    cost_usd: 220,
    raised_usd: 0,
    cadence: "one-time",
    why_this:
      "Cheap line item; included to demonstrate the Wish List handles small directed gifts (any supporter can fully fund this) alongside the larger ones. Quiet-click mouse and backlit keyboard, multi-device pairing for the workstation + Mac Studio + travel laptop.",
    what_it_unlocks:
      "An end to swapping a single keyboard between three machines. Faster context switches; fewer cable yanks.",
    purchase_url:
      "https://www.amazon.com/Logitech-MX-Master-Wireless-Mouse/dp/B0C6YRL6GN",
    vendor: "Amazon",
    accepts_partial: true,
    accepts_in_kind: true,
    status: "open",
  },
  {
    id: "501c3-counsel",
    name: "501(c)(3) legal counsel — Form 1023 + DE conversion",
    short: "Close the gap that keeps gifts non-deductible",
    category: "legal",
    cost_usd: 4_500,
    raised_usd: 0,
    cadence: "one-time",
    why_this:
      "The single highest-leverage administrative item. Until this completes, gifts under stewardship are not tax-deductible and the Foundation cannot apply to AWS Imagine, TechSoup, or most institutional grants. Closing this gap is the precondition for everything else on the funding roadmap.",
    what_it_unlocks:
      "Tax-deductible giving for US donors. Eligibility for the grant pipeline laid out on this page. End of the stewardship period; transition of funds and obligations to the Foundation itself.",
    vendor: "Counsel — to be retained",
    accepts_partial: true,
    accepts_in_kind: false,
    status: "open",
  },
  {
    id: "social-media-manager",
    name: "Sponsored Social Media Manager — six months",
    short: "Distribution coverage for the protocol",
    category: "services",
    cost_usd: 7_200,
    raised_usd: 0,
    cadence: "monthly",
    duration_months: 6,
    why_this:
      "Distribution is the binding constraint on protocol adoption. Founder-led posting is not a substitute. Six months of sponsored coverage gives the protocol a fair shot at organic reach across the venues where the conversation is actually happening.",
    what_it_unlocks:
      "Consistent presence across LinkedIn, X/Bluesky, and Mastodon during the ICANN evaluation window. Accepts partial gifts at $1,200/mo so a supporter can fund a single month.",
    vendor: "Contractor — to be retained",
    accepts_partial: true,
    accepts_in_kind: false,
    status: "open",
  },
] as const;

export const WISHLIST_TOTAL_USD: number = WISHLIST_ITEMS.reduce(
  (sum, item) => sum + item.cost_usd,
  0,
);

export const WISHLIST_RAISED_USD: number = WISHLIST_ITEMS.reduce(
  (sum, item) => sum + item.raised_usd,
  0,
);

export function wishlistByCategory(category: WishlistCategory): WishlistItem[] {
  return WISHLIST_ITEMS.filter((item) => item.category === category);
}

export const WISHLIST_CATEGORY_LABEL: Record<WishlistCategory, string> = {
  compute: "Compute",
  infrastructure: "Infrastructure",
  operations: "Office & operations",
  legal: "Legal",
  services: "Services",
};

/** Canonical memo code an inbound wire should reference. */
export function memoCodeFor(item: Pick<WishlistItem, "id">): string {
  return `DPF-WISH-${item.id.toUpperCase()}`;
}
